/**
 * The PXL score set, and the one place it is validated.
 *
 * Potential, Exposure and Level of effort, each rated 1 to 5. The type was declared
 * separately in Importance, PXLRatingFlow and PXLScoreDisplay, which is the same
 * drift that had four copies of PowerAnalysisResult across three packages before it
 * was consolidated.
 */
export type PxlScores = { p: number; x: number; l: number };

const isScore = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value > 0;

/**
 * Returns a complete score set, or null if the stored value is absent or incomplete,
 * so everything downstream can assume all three dimensions exist.
 *
 * pxl_scores is a nullable json column, so this can arrive as null, as undefined when a
 * caller sends a partial experiment, or as a partial object written by an older client.
 * A `!== null` check alone let undefined through and then threw on `.p`, taking the
 * whole Info tab down.
 */
export const parsePxlScores = (raw: unknown): PxlScores | null => {
  if (!raw || typeof raw !== 'object') return null;

  const { p, x, l } = raw as Partial<PxlScores>;
  if (!isScore(p) || !isScore(x) || !isScore(l)) return null;

  return { p, x, l };
};

/** The 0 to 10 importance the score set implies, which is what the list sorts on. */
export { importanceFromScores } from 'helpers/pxl-importance';
